import { osnovaApi } from '../base';
import { parseResponse } from '../parseResponse';
import type { AppDispatch } from '@app/store';
import type {
  FleetCategory,
  FleetImport,
  FleetImportResult,
  FleetModel,
  FleetModelData,
  FleetModelDetail,
  FleetModelFilter,
  FleetRevision,
  FleetUnit,
  FleetUnitData,
} from '../types';

/**
 * Справочник техники платформы (`/api/v1/fleet`, ТЗ AI-Economist-2026 ред. 2.1,
 * п. 6.1, 7.1). Общий реестр, а не таблица одного агента: читать могут все с
 * доступом к любому агенту, править и импортировать — право записи в Экономиста.
 *
 * Теги: `{Fleet, 'LIST'}` — список моделей, `{Fleet, 'CATEGORIES'}` —
 * категории с числом моделей, `{Fleet, <id модели>}` — карточка и её журнал.
 * Мутации модели сразу кладут ответ в кеш карточки, чтобы экран не ждал
 * повторного GET, и инвалидируют список и категории.
 */

// `as const` — литерал 'Fleet' вместо string: иначе RTK не узнает тег из tagTypes.
const LIST = { type: 'Fleet', id: 'LIST' } as const;
const CATEGORIES = { type: 'Fleet', id: 'CATEGORIES' } as const;

export const fleetApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    listFleetCategories: b.query<{ items: FleetCategory[] }, void>({
      query: () => '/fleet/categories',
      providesTags: [CATEGORIES],
    }),

    /** Список моделей с ключевыми полями; паспорт в списке пустой. */
    listFleetModels: b.query<{ items: FleetModel[] }, FleetModelFilter>({
      query: (params) => ({ url: '/fleet/models', params }),
      providesTags: [LIST],
    }),

    /** Карточка: паспорт целиком и единицы техники. */
    getFleetModel: b.query<FleetModelDetail, string>({
      query: (id) => `/fleet/models/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Fleet', id }],
    }),

    /** Журнал правок модели «было → стало», новые сверху. */
    listFleetModelRevisions: b.query<{ items: FleetRevision[] }, string>({
      query: (id) => `/fleet/models/${id}/revisions`,
      providesTags: (_r, _e, id) => [{ type: 'Fleet', id }],
    }),

    /** Создать модель; 409 — модель с таким наименованием уже есть. */
    createFleetModel: b.mutation<{ model: FleetModel }, FleetModelData>({
      query: (body) => ({ url: '/fleet/models', method: 'POST', body }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          void dispatch(
            fleetApi.util.upsertQueryData('getFleetModel', data.model.id, {
              model: data.model,
              units: [],
            }),
          );
        } catch {
          /* ошибку показывает форма через unwrap() */
        }
      },
      invalidatesTags: [LIST, CATEGORIES],
    }),

    /**
     * Изменить модель. `expected_updated_at` — `updated_at` открытой карточки:
     * если модель успели изменить, backend отвечает 409 и ничего не пишет.
     */
    updateFleetModel: b.mutation<
      { model: FleetModel },
      { id: string; data: FleetModelData; expected_updated_at: string }
    >({
      query: ({ id, data, expected_updated_at }) => ({
        url: `/fleet/models/${id}`,
        method: 'PUT',
        body: { ...data, expected_updated_at },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeModel(dispatch, queryFulfilled),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Fleet', id }, LIST, CATEGORIES],
    }),

    /** Отправить в архив (удаления нет). */
    archiveFleetModel: b.mutation<{ model: FleetModel }, string>({
      query: (id) => ({ url: `/fleet/models/${id}/archive`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeModel(dispatch, queryFulfilled),
      invalidatesTags: (_r, _e, id) => [{ type: 'Fleet', id }, LIST, CATEGORIES],
    }),

    /** Вернуть из архива; 409 — модель с тем же наименованием уже в работе. */
    restoreFleetModel: b.mutation<{ model: FleetModel }, string>({
      query: (id) => ({ url: `/fleet/models/${id}/restore`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeModel(dispatch, queryFulfilled),
      invalidatesTags: (_r, _e, id) => [{ type: 'Fleet', id }, LIST, CATEGORIES],
    }),

    createFleetUnit: b.mutation<{ unit: FleetUnit }, { modelId: string; data: FleetUnitData }>({
      query: ({ modelId, data }) => ({
        url: `/fleet/models/${modelId}/units`,
        method: 'POST',
        body: data,
      }),
      async onQueryStarted({ modelId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          void dispatch(
            fleetApi.util.updateQueryData('getFleetModel', modelId, (draft) => {
              draft.units.push(data.unit);
            }),
          );
        } catch {
          /* ошибку показывает форма единицы */
        }
      },
      invalidatesTags: (_r, _e, { modelId }) => [{ type: 'Fleet', id: modelId }],
    }),

    updateFleetUnit: b.mutation<
      { unit: FleetUnit },
      { id: string; modelId: string; data: FleetUnitData }
    >({
      query: ({ id, data }) => ({ url: `/fleet/units/${id}`, method: 'PUT', body: data }),
      async onQueryStarted({ modelId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          void dispatch(
            fleetApi.util.updateQueryData('getFleetModel', modelId, (draft) => {
              draft.units = draft.units.map((u) => (u.id === data.unit.id ? data.unit : u));
            }),
          );
        } catch {
          /* ошибку показывает форма единицы */
        }
      },
      invalidatesTags: (_r, _e, { modelId }) => [{ type: 'Fleet', id: modelId }],
    }),

    /**
     * Предпросмотр импорта книги ЭММ: multipart с полем `file` (.xlsx). Ничего
     * не пишет в справочник; 400 с понятным текстом — книга не та.
     */
    previewFleetImport: b.mutation<{ import: FleetImport }, FormData>({
      query: (form) => ({ url: '/fleet/import/preview', method: 'POST', body: form }),
    }),

    /** Применить предпросмотр: `accept` — названия моделей; без него — все. */
    applyFleetImport: b.mutation<{ result: FleetImportResult }, { id: string; accept?: string[] }>({
      query: ({ id, accept }) => ({
        url: `/fleet/import/${id}/apply`,
        method: 'POST',
        body: accept ? { accept } : {},
      }),
      invalidatesTags: ['Fleet'],
    }),

    /**
     * Выгрузка справочника в xlsx в формате листа «Справочник (паспорт
     * техники)» — экономист правит в Excel и загружает обратно (п. 6.1.3).
     *
     * Файл в хранилище не попадает: Blob в Redux — несериализуемое значение.
     * Запрос идёт тем же baseQuery (токен, повтор чтения), а наружу отдаётся
     * object URL — экран сохраняет файл и сразу его освобождает. Ошибка
     * приходит обычным JSON (`{error: {code, message}}`) — `apiErrorText`.
     */
    exportFleetXlsx: b.mutation<{ url: string }, void>({
      async queryFn(_arg, _api, _extra, baseQuery) {
        const res = await baseQuery({
          url: '/fleet/export.xlsx',
          cache: 'no-store',
          responseHandler: (response) => (response.ok ? response.blob() : parseResponse(response)),
        });
        if (res.error) return { error: res.error };
        if (!(res.data instanceof Blob)) {
          return { error: { status: 'CUSTOM_ERROR', error: 'Сервер вернул не файл.' } };
        }
        return { data: { url: URL.createObjectURL(res.data) } };
      },
    }),

    /** Отменить предпросмотр (в справочник ничего не попадает). */
    discardFleetImport: b.mutation<{ id: string; status: string }, string>({
      query: (id) => ({ url: `/fleet/import/${id}/discard`, method: 'POST' }),
    }),
  }),
  overrideExisting: false,
});

/** Ответ мутации модели — сразу в кеш карточки, единицы остаются прежними. */
async function storeModel(
  dispatch: AppDispatch,
  queryFulfilled: Promise<{ data: { model: FleetModel } }>,
): Promise<void> {
  try {
    const { data } = await queryFulfilled;
    void dispatch(
      fleetApi.util.updateQueryData('getFleetModel', data.model.id, (draft) => {
        draft.model = data.model;
      }),
    );
  } catch {
    /* ошибку показывает экран через unwrap() */
  }
}

export const {
  useListFleetCategoriesQuery,
  useListFleetModelsQuery,
  useGetFleetModelQuery,
  useLazyGetFleetModelQuery,
  useListFleetModelRevisionsQuery,
  useCreateFleetModelMutation,
  useUpdateFleetModelMutation,
  useArchiveFleetModelMutation,
  useRestoreFleetModelMutation,
  useCreateFleetUnitMutation,
  useUpdateFleetUnitMutation,
  usePreviewFleetImportMutation,
  useApplyFleetImportMutation,
  useDiscardFleetImportMutation,
  useExportFleetXlsxMutation,
} = fleetApi;
